import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Avatar, IconButton, Tooltip, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Alert,
  Grid, Badge
} from '@mui/material';
import {
  Search, CheckCircle, Cancel, HourglassEmpty, Person, BadgeOutlined,
  ReceiptLong, AccountBalanceWallet, MoneyOff, CalendarToday, NoteAlt,
  FilterList, EventNote, Refresh, AssignmentLate, Close, Visibility,
  CheckCircleOutline, CancelOutlined, History
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { formatCurrency } from '../Message';
import {
  loadAllWriteOffRequests,  // POST – load all requests (admin view)
  approveWriteOff,          // POST – approve a request
  rejectWriteOff,           // POST – reject a request
} from '../BaseURL';

/*
 ┌──────────────────────────────────────────────────────────────────────────────
 │  ADD THESE 3 LINES TO YOUR BaseURL.js:
 │
 │  export const loadAllWriteOffRequests = BASE_URL + '/loadAllWriteOffRequests';
 │  export const approveWriteOff         = BASE_URL + '/approveWriteOff';
 │  export const rejectWriteOff          = BASE_URL + '/rejectWriteOff';
 └──────────────────────────────────────────────────────────────────────────────
*/

// ─── Status chip ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    pending:  { label: 'Pending',  color: '#b45309', bg: '#fef3c7', Icon: HourglassEmpty },
    approved: { label: 'Approved', color: '#065f46', bg: '#d1fae5', Icon: CheckCircle    },
    rejected: { label: 'Rejected', color: '#991b1b', bg: '#fee2e2', Icon: Cancel         },
  };
  const s = map[status] || map.pending;
  return (
    <Chip
      icon={<s.Icon sx={{ fontSize: '15px !important', color: `${s.color} !important` }} />}
      label={s.label} size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 13,
        border: `1px solid ${s.color}33`, px: 0.5 }}
    />
  );
};

// ─── Detail row inside the review dialog ─────────────────────────────────────
const DetailRow = ({ icon, label, value, highlight }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 2, px: 2.5,
    bgcolor: highlight ? 'rgba(6,95,70,0.06)' : 'transparent' }}>
    <Box sx={{
      color: highlight ? '#065f46' : '#6ee7b7',
      mt: '2px', flexShrink: 0, p: 1, borderRadius: 1.5,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: highlight ? 'rgba(6,95,70,0.12)' : 'rgba(110,231,183,0.13)',
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 20, ...icon.props?.sx } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, fontSize: '0.68rem', mb: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{
        color: highlight ? '#065f46' : '#1f2937',
        fontWeight: highlight ? 800 : 600,
        fontSize: '1rem',
        wordBreak: 'break-word',
        lineHeight: 1.4,
      }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  </Box>
);

// ─── Summary stat card ────────────────────────────────────────────────────────
const StatCard = ({ label, count, amount, color, bg, Icon }) => (
  <Card variant="outlined" sx={{ borderColor: color, borderRadius: 2, bgcolor: bg }}>
    <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
          <Icon sx={{ fontSize: 20, color: '#fff' }} />
        </Avatar>
        <Box>
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ color, fontWeight: 800, lineHeight: 1.2 }}>{count}</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 11 }}>
            {formatCurrency(amount)}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Review / Approve / Reject Dialog ────────────────────────────────────────
const ReviewDialog = ({ open, onClose, record, currentUser, onApprove, onReject, processing }) => {
  const [rejectNotes, setRejectNotes] = useState('');
  const [mode, setMode] = useState('view'); // 'view' | 'reject'

  useEffect(() => {
    if (open) { setRejectNotes(''); setMode('view'); }
  }, [open, record?.id]);

  if (!record) return null;

  const handleApprove = () => onApprove(record, currentUser);
  const handleReject  = () => {
    if (!rejectNotes.trim()) {
      Swal.fire({ title: 'Notes Required', text: 'Please enter a reason for rejection.', icon: 'warning', confirmButtonColor: '#065f46' });
      return;
    }
    onReject(record, rejectNotes, currentUser);
  };

  // capitalize reason label
  const reasonLabel = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Dialog open={open} onClose={!processing ? onClose : undefined} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%)',
        px: 3, pt: 3, pb: 2.5, position: 'relative', borderBottom: '3px solid #6ee7b7',
      }}>
        <IconButton onClick={onClose} disabled={processing} size="small"
          sx={{ position: 'absolute', top: 10, right: 10, color: '#6ee7b7',
            bgcolor: 'rgba(110,231,183,0.1)', '&:hover': { color: '#fff', bgcolor: 'rgba(110,231,183,0.2)' } }}>
          <Close fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 4 }}>
          <Avatar sx={{ bgcolor: 'rgba(110,231,183,0.18)', width: 54, height: 54, border: '2px solid #6ee7b7', flexShrink: 0 }}>
            <AssignmentLate sx={{ color: '#6ee7b7', fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.2, letterSpacing: 0.3 }}>
              Review Write-off Request
            </Typography>
            <Typography variant="body2" sx={{ color: '#6ee7b7', mt: 0.4, fontWeight: 500 }}>
              Approve or reject this request
            </Typography>
          </Box>
        </Box>

        {/* Patient chips */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<Person sx={{ color: '#065f46 !important', fontSize: 14 }} />}
            label={record.patient_name} size="small"
            sx={{ bgcolor: '#6ee7b7', color: '#065f46', fontWeight: 700, fontSize: 12 }}
          />
          <Chip
            icon={<BadgeOutlined sx={{ color: '#6ee7b7 !important', fontSize: 13 }} />}
            label={record.patient_id} size="small"
            sx={{ bgcolor: 'rgba(110,231,183,0.15)', color: '#6ee7b7', fontWeight: 600, fontSize: 12,
              border: '1px solid rgba(110,231,183,0.4)' }}
          />
          <StatusChip status={record.status} />
        </Box>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{ p: 0, bgcolor: '#fafafa' }}>

        {/* Patient & Bill info grid */}
        <Box sx={{ mx: 3, mt: 3, mb: 2.5, borderRadius: 2, border: '1px solid #6ee7b7',
          bgcolor: '#fff', overflow: 'hidden' }}>

          {/* Section label */}
          <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
            <Typography sx={{ color: '#065f46', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 1, fontSize: 11 }}>
              Patient &amp; Bill Information
            </Typography>
          </Box>
          <Divider sx={{ borderColor: '#d1fae5' }} />

          <Grid container>
            <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
              <DetailRow icon={<BadgeOutlined />} label="Patient ID" value={record.patient_id} />
            </Grid>
            <Grid item xs={6}>
              <DetailRow icon={<Person />} label="Patient Name" value={record.patient_name} />
            </Grid>
            <Grid item xs={12}><Divider sx={{ borderColor: '#d1fae5' }} /></Grid>
            <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
              <DetailRow icon={<ReceiptLong />} label="Invoice Number" value={record.invoice_number} />
            </Grid>
            <Grid item xs={6}>
              <DetailRow icon={<CalendarToday />} label="Request Date" value={record.request_date} />
            </Grid>
            <Grid item xs={12}><Divider sx={{ borderColor: '#d1fae5' }} /></Grid>
            <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
              <DetailRow icon={<AccountBalanceWallet />} label="Total Bill" value={formatCurrency(record.total_bill)} />
            </Grid>
            <Grid item xs={6}>
              <DetailRow icon={<AccountBalanceWallet />} label="Outstanding Amount" value={formatCurrency(record.outstanding_amt)} />
            </Grid>
          </Grid>

          {/* Highlighted write-off row */}
          <Divider sx={{ borderColor: '#6ee7b7' }} />
          <Grid container sx={{ bgcolor: 'rgba(6,95,70,0.05)' }}>
            <Grid item xs={6} sx={{ borderRight: '1px solid #6ee7b7' }}>
              <DetailRow icon={<MoneyOff />} label="Write-off Amount" value={formatCurrency(record.writeoff_amount)} highlight />
            </Grid>
            <Grid item xs={6}>
              <DetailRow icon={<AssignmentLate />} label="Reason" value={reasonLabel(record.reason)} highlight />
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: '#d1fae5' }} />
          <Grid container>
            <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
              <DetailRow icon={<Person />} label="Requested By" value={record.requested_by} />
            </Grid>
            <Grid item xs={6}>
              <DetailRow icon={<EventNote />} label="Submitted On" value={record.created_at?.split('T')[0]} />
            </Grid>
          </Grid>

          {record.notes && (
            <>
              <Divider sx={{ borderColor: '#d1fae5' }} />
              <DetailRow icon={<NoteAlt />} label="Requester Notes" value={record.notes} />
            </>
          )}

          {/* Review info for non-pending requests */}
          {record.status !== 'pending' && record.reviewed_by && (
            <>
              <Divider sx={{ borderColor: '#d1fae5' }} />
              <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
                <Typography sx={{ color: record.status === 'approved' ? '#065f46' : '#991b1b',
                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
                  {record.status === 'approved' ? 'Approval Details' : 'Rejection Details'}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: '#d1fae5' }} />
              <Grid container>
                <Grid item xs={6} sx={{ borderRight: '1px solid #d1fae5' }}>
                  <DetailRow icon={<Person />} label="Reviewed By" value={record.reviewed_by} />
                </Grid>
                <Grid item xs={6}>
                  <DetailRow icon={<CalendarToday />} label="Reviewed On" value={record.reviewed_at?.split('T')[0]} />
                </Grid>
              </Grid>
              {record.review_notes && (
                <>
                  <Divider sx={{ borderColor: '#d1fae5' }} />
                  <DetailRow icon={<NoteAlt />} label="Review Notes" value={record.review_notes} />
                </>
              )}
            </>
          )}
        </Box>

        {/* ── Reject mode: notes input ── */}
        {mode === 'reject' && (
          <Box sx={{ mx: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, mb: 2,
              borderRadius: 2, bgcolor: '#fff5f5', border: '1px solid #fca5a5' }}>
              <Cancel sx={{ color: '#dc2626', fontSize: 22, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600, fontSize: 14 }}>
                You are about to <strong>reject</strong> this write-off request. A reason is required.
              </Typography>
            </Box>
            <TextField
              label="Rejection Reason *" multiline rows={3} fullWidth
              value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Explain why this request is being rejected…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <NoteAlt sx={{ color: '#065f46', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#065f46' } },
                '& .MuiInputLabel-root.Mui-focused': { color: '#065f46' },
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f0fdf4', borderTop: '2px solid #6ee7b7', gap: 1 }}>
        <Button onClick={onClose} disabled={processing} variant="outlined"
          sx={{ borderColor: '#6ee7b7', color: '#065f46', fontWeight: 600,
            '&:hover': { bgcolor: 'rgba(110,231,183,0.1)', borderColor: '#065f46' } }}>
          Close
        </Button>

        {/* Pending + view mode: show Reject + Approve */}
        {record.status === 'pending' && mode === 'view' && (
          <>
            <Button onClick={() => setMode('reject')} disabled={processing} variant="outlined"
              startIcon={<CancelOutlined />}
              sx={{ borderColor: '#dc2626', color: '#dc2626', fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(220,38,38,0.06)' } }}>
              Reject
            </Button>
            <Button onClick={handleApprove} disabled={processing} variant="contained"
              startIcon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckCircleOutline />}
              sx={{ background: 'linear-gradient(135deg, #064e3b, #065f46)', fontWeight: 700, px: 3,
                '&:hover': { background: 'linear-gradient(135deg, #022c22, #064e3b)' },
                '&:disabled': { bgcolor: '#e0e0e0', color: '#bdbdbd' } }}>
              {processing ? 'Approving…' : 'Approve Request'}
            </Button>
          </>
        )}

        {/* Pending + reject mode: show Back + Confirm Reject */}
        {record.status === 'pending' && mode === 'reject' && (
          <>
            <Button onClick={() => setMode('view')} disabled={processing} variant="text"
              sx={{ color: '#065f46', fontWeight: 600, '&:hover': { bgcolor: 'rgba(6,95,70,0.06)' } }}>
              ← Back
            </Button>
            <Button onClick={handleReject} disabled={processing} variant="contained"
              startIcon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Cancel />}
              sx={{ bgcolor: '#dc2626', fontWeight: 700, px: 3,
                '&:hover': { bgcolor: '#b91c1c' }, '&:disabled': { bgcolor: '#e0e0e0', color: '#bdbdbd' } }}>
              {processing ? 'Rejecting…' : 'Confirm Reject'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
function ApproveRequestWriteoff() {
  const [requests, setRequests]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [processing, setProcessing]     = useState(false);

  // Filters
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');

  // Review dialog
  const [reviewRecord, setReviewRecord] = useState(null);
  const [reviewOpen, setReviewOpen]     = useState(false);

  const [currentUser, setCurrentUser]   = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('userInfo');
    if (saved) { const info = JSON.parse(saved); setCurrentUser(info.name || ''); }
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.post(loadAllWriteOffRequests);
      if (res.data.success) setRequests(res.data.results || []);
      else setRequests([]);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load write-off requests', icon: 'error' });
      setRequests([]);
    } finally { setLoading(false); }
  };

  // ── Approve handler ─────────────────────────────────────────────────────────
  const handleApprove = useCallback(async (record, reviewer) => {
    const confirm = await Swal.fire({
      title: 'Approve Write-off?',
      html: `<p style="color:#555;margin-bottom:8px;">Are you sure you want to approve this write-off request for <strong>${record.patient_name}</strong>?</p>
             <p style="color:#065f46;font-weight:700;font-size:16px;">${formatCurrency(record.writeoff_amount)}</p>
             <p style="color:#888;font-size:13px;margin-top:6px;">This will update the patient's debt balance and record the write-off in day income.</p>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#065f46', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Approve', cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setProcessing(true);
    try {
      const res = await axios.post(approveWriteOff, {
        id:              record.id,
        patient_id:      record.patient_id,
        patient_name:    record.patient_name,
        writeoff_amount: record.writeoff_amount,
        reviewed_by:     reviewer,
      });

      if (!res.data.success) throw new Error(res.data.message || 'Approval failed');

      setReviewOpen(false);
      await loadRequests();
      await Swal.fire({
        title: 'Approved!',
        html: `<p style="color:#555;">Write-off of <strong>${formatCurrency(record.writeoff_amount)}</strong> for <strong>${record.patient_name}</strong> has been approved.</p>
               <p style="color:#888;font-size:13px;margin-top:6px;">Debt balance and day income updated.</p>`,
        icon: 'success', confirmButtonColor: '#065f46',
      });
    } catch (err) {
      await Swal.fire({ title: 'Approval Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally { setProcessing(false); }
  }, []);

  // ── Reject handler ──────────────────────────────────────────────────────────
  const handleReject = useCallback(async (record, notes, reviewer) => {
    setProcessing(true);
    try {
      const res = await axios.post(rejectWriteOff, {
        id:           record.id,
        reviewed_by:  reviewer,
        review_notes: notes,
      });

      if (!res.data.success) throw new Error(res.data.message || 'Rejection failed');

      setReviewOpen(false);
      await loadRequests();
      await Swal.fire({
        title: 'Rejected',
        text: `Write-off request for ${record.patient_name} has been rejected.`,
        icon: 'info', confirmButtonColor: '#1976d2',
      });
    } catch (err) {
      await Swal.fire({ title: 'Rejection Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally { setProcessing(false); }
  }, []);

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = requests;

    // Status filter
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);

    // Date range
    if (startDate) list = list.filter((r) => {
      const d = new Date(r.request_date || r.created_at);
      return d >= new Date(startDate);
    });
    if (endDate) list = list.filter((r) => {
      const d = new Date(r.request_date || r.created_at);
      return d <= new Date(endDate);
    });

    // Search by name or patient ID
    if (searchTerm.trim()) {
      const sl = searchTerm.toLowerCase();
      list = list.filter((r) =>
        (r.patient_name || '').toLowerCase().includes(sl) ||
        (r.patient_id   || '').toLowerCase().includes(sl)
      );
    }

    return list;
  }, [requests, statusFilter, startDate, endDate, searchTerm]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    pending:  { count: requests.filter(r=>r.status==='pending').length,  amount: requests.filter(r=>r.status==='pending').reduce((s,r)=>s+parseFloat(r.writeoff_amount||0),0) },
    approved: { count: requests.filter(r=>r.status==='approved').length, amount: requests.filter(r=>r.status==='approved').reduce((s,r)=>s+parseFloat(r.writeoff_amount||0),0) },
    rejected: { count: requests.filter(r=>r.status==='rejected').length, amount: requests.filter(r=>r.status==='rejected').reduce((s,r)=>s+parseFloat(r.writeoff_amount||0),0) },
  }), [requests]);

  const clearFilters = () => { setSearchTerm(''); setStartDate(''); setEndDate(''); setStatusFilter('pending'); };

  const handleOpenReview = (record) => { setReviewRecord(record); setReviewOpen(true); };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: 1 }}>

      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          Write-off Requests
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Review, approve, or reject pending debt write-off requests
        </Typography>
      </Box>

      {/* Summary stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Pending',  ...stats.pending,  color: '#b45309', bg: '#fef3c7', Icon: HourglassEmpty, filter: 'pending'  },
          { label: 'Approved', ...stats.approved, color: '#065f46', bg: '#d1fae5', Icon: CheckCircle,    filter: 'approved' },
          { label: 'Rejected', ...stats.rejected, color: '#991b1b', bg: '#fee2e2', Icon: Cancel,         filter: 'rejected' },
        ].map((s) => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Box onClick={() => setStatusFilter(s.filter)} sx={{ cursor: 'pointer',
              transform: statusFilter === s.filter ? 'scale(1.02)' : 'scale(1)',
              transition: 'transform 0.15s ease',
              outline: statusFilter === s.filter ? `2px solid ${s.color}` : 'none',
              borderRadius: 2 }}>
              <StatCard {...s} />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField fullWidth placeholder="Search by name or patient ID…" value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Requests</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date range */}
            <Grid item xs={12} md={2.5}>
              <TextField fullWidth type="date" label="From Date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField fullWidth type="date" label="To Date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Actions */}
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={clearFilters} fullWidth
                  sx={{ borderColor: '#bdbdbd', color: '#555' }}>
                  Clear
                </Button>
                <Button variant="contained" onClick={loadRequests} startIcon={<Refresh />} fullWidth
                  sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Active filter chips */}
          {(searchTerm || startDate || endDate || statusFilter !== 'pending') && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <FilterList sx={{ color: '#6b7280', fontSize: 18 }} />
              {statusFilter !== 'all' && (
                <Chip label={`Status: ${statusFilter}`} size="small" onDelete={() => setStatusFilter('all')}
                  sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} />
              )}
              {searchTerm && (
                <Chip label={`Search: "${searchTerm}"`} size="small" onDelete={() => setSearchTerm('')}
                  sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 }} />
              )}
              {startDate && (
                <Chip label={`From: ${startDate}`} size="small" onDelete={() => setStartDate('')}
                  sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600 }} />
              )}
              {endDate && (
                <Chip label={`To: ${endDate}`} size="small" onDelete={() => setEndDate('')}
                  sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600 }} />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Requests
              <Chip label={filtered.length} size="small"
                sx={{ ml: 1.5, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: 13 }} />
            </Typography>
            {loading && <CircularProgress size={22} sx={{ color: '#1976d2' }} />}
          </Box>

          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress size={36} sx={{ color: '#1976d2' }} />
              <Typography sx={{ mt: 2, color: '#6b7280', fontWeight: 500 }}>Loading requests…</Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <AssignmentLate sx={{ fontSize: 52, color: '#d1d5db', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#6b7280', fontWeight: 600 }}>No requests found</Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>Try adjusting your filters</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['#', 'Patient ID', 'Patient Name', 'Invoice', 'Request Date',
                      'Outstanding', 'Write-off Amt', 'Reason', 'Requested By', 'Status', 'Actions'].map((h) => (
                      <TableCell key={h}
                        sx={{ fontWeight: 700, fontSize: 14, color: '#374151',
                          borderBottom: '2px solid #e0e0e0', py: 1.8, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r, i) => (
                    <TableRow key={r.id}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f9ff' },
                        borderLeft: r.status === 'pending' ? '4px solid #f59e0b'
                          : r.status === 'approved' ? '4px solid #10b981' : '4px solid #ef4444' }}>
                      <TableCell sx={{ fontSize: 14, color: '#6b7280', fontWeight: 600, py: 2 }}>
                        {i + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#374151', py: 2, fontWeight: 600 }}>
                        {r.patient_id}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 34, height: 34, bgcolor: '#1976d2', fontSize: 14, fontWeight: 700 }}>
                            {(r.patient_name || 'P').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                            {r.patient_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#6b7280', py: 2 }}>{r.invoice_number}</TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#374151', py: 2, whiteSpace: 'nowrap' }}>
                        {r.request_date}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#374151', py: 2 }}>
                        {formatCurrency(r.outstanding_amt)}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#065f46' }}>
                          {formatCurrency(r.writeoff_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#374151', py: 2, maxWidth: 160 }}>
                        <Tooltip title={(r.reason || '').replace(/_/g, ' ')}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', maxWidth: 150, cursor: 'default', fontSize: 14 }}>
                            {(r.reason || '').replace(/_/g, ' ')}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 14, color: '#374151', py: 2 }}>{r.requested_by}</TableCell>
                      <TableCell sx={{ py: 2 }}><StatusChip status={r.status} /></TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={r.status === 'pending' ? 'Review Request' : 'View Details'}>
                          <IconButton onClick={() => handleOpenReview(r)}
                            sx={{ color: '#1976d2', bgcolor: 'rgba(25,118,210,0.06)',
                              '&:hover': { bgcolor: 'rgba(25,118,210,0.14)' } }}>
                            {r.status === 'pending' ? <AssignmentLate sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <ReviewDialog
        open={reviewOpen}
        onClose={() => { if (!processing) { setReviewOpen(false); setReviewRecord(null); } }}
        record={reviewRecord}
        currentUser={currentUser}
        onApprove={handleApprove}
        onReject={handleReject}
        processing={processing}
      />
    </Box>
  );
}

export default ApproveRequestWriteoff;